import { useEffect, useState } from 'react'
import { API } from '../../../../api'
import { IQDialog, Preloader } from '../../../common'
import Button from '@mui/material/Button'
import { colors, Stack } from '@mui/material'
import { TADMembershipGroup } from '../../../../type'
import { trimAndReplaceWhiteSpaces } from '../../../../utility/string-format'
import { IADMembershipGroup } from '..'

interface IProps {
  isShow: boolean
  name: string
  oldName: string
  description: string
  groups: IADMembershipGroup[]
  id: string
  type: 'INT' | 'EXT' | 'TEMPLATE'
  templateGroups: Record<string, { name: string; description: string }[]>
  onSuccess: () => void
  onCancel: () => void
}

export const MembershipUpdate = ({ isShow, name, id, oldName, description, groups, type, templateGroups, onSuccess, onCancel }: IProps) => {
  useEffect(() => {
    const fetchMembershipDetails = async () => {
      try {
        const {
          Members: { Users },
        } = await API.ADManagement.covered_entity.detail(name as string)
        setUsers(Users)
      } catch (error) {
        console.error('Error fetching membership details:', error)
      }
    }

    if (isShow && id && type !== 'TEMPLATE') {
      fetchMembershipDetails()
    }
  }, [name, isShow, id, type])

  const [isLoading, setIsLoading] = useState(false)
  const [users, setUsers] = useState<TADMembershipGroup[]>([])

  const handleUpdate = async () => {
    try {
      setIsLoading(true)
      if (type === 'TEMPLATE') {
        if (oldName === name) {
          await API.ADManagement.membership.updateTemplate(id, templateGroups)
        } else {
          const trimmedName = trimAndReplaceWhiteSpaces(name)
          await API.ADManagement.membership.updateTemplate(id, templateGroups, trimmedName)
        }
      } else {
        await API.ADManagement.membership.update(
          oldName,
          trimAndReplaceWhiteSpaces(name),
          trimAndReplaceWhiteSpaces(description),
          groups.filter((group) => !group.nested).map((group) => group.name),
          type,
        )
      }
      return true
    } catch {
      onCancel()
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const renderUpdateActions = () => (
    <Stack
      direction='row'
      spacing={2}
      justifyContent='space-between'
      sx={{
        width: '100%',
      }}
    >
      <Button
        onClick={onCancel}
        variant='outlined'
        data-testid={`${type === 'TEMPLATE' ? 'template' : 'membership'}-update-cancel`}
      >
        Cancel
      </Button>
      <Button
        variant='contained'
        disabled={isLoading}
        onClick={async () => {
          const success = await handleUpdate()
          if (success) onSuccess()
        }}
        data-testid={`${type === 'TEMPLATE' ? 'template' : 'membership'}-update-save`}
      >
        Update
      </Button>
    </Stack>
  )

  return (
    <IQDialog
      isShow={isShow}
      title={`Are you sure you want to update this ${type === 'TEMPLATE' ? 'Template' : 'Membership'}?`}
      content={
        isLoading ? (
          <Preloader
            size='sm'
            style={{ display: 'block', margin: '0 auto' }}
          />
        ) : users.length > 0 ? (
          <span style={{ display: 'block' }}>
            <span style={{ fontWeight: 'bold', color: colors.red[500] }}>Please Note!</span> Following Users will be affected by this
            change:{' '}
            {users.map((user, index) => (
              <span key={index}>
                <b>
                  {user.DisplayName || ''} ({user?.MemberName}){index < users.length - 1 && ', '}
                </b>
              </span>
            ))}
          </span>
        ) : (
          false
        )
      }
      actions={renderUpdateActions()}
      onClose={onCancel}
      testPrefix='membership-update'
    />
  )
}
